/**
 * @Copyright 2014-2018 DataVoke, Inc. All rights reserved.
 */

'use strict';

class ServiceEndpointDef {
    /**
     * Create a new service endpoint definition object.
     * @constructor
     * @param {string} serviceName - The name of the service to call.
     * @param {string} endpointName - The name of the endpoint to call for the service specified.
     * @param {boolean} [authRequired = true] - TRUE, this endpoint will require authentication; FALSE, the endpoint can be called while unauthenticated.
     */
    constructor (serviceName, endpointName, authRequired = true) {
        this.svc = serviceName;
        this.fn = endpointName;
        this.authRequired = authRequired;
    }
}

class Endpoints extends Array {
    /**
     * Create the endpoints object which contains properties and is an array of endpoint definition objects.
     * @constructor
     */
    constructor () {
        super();

        //#region Common endpoints
        this.ping = new ServiceEndpointDef('Support', 'Ping', false);
        this.push(this.ping);

        this.getUserInfoValues = new ServiceEndpointDef('User', 'GetUserInfoValues');
        this.push(this.getUserInfoValues);

        this.getConfig = new ServiceEndpointDef('Config', 'GetConfig', false);
        this.push(this.getConfig);

        this.keepAlive = new ServiceEndpointDef('Support', 'KeepAlive', false);
        this.push(this.keepAlive);

        this.proxyWebResource = new ServiceEndpointDef('Support', 'ProxyWebResource');
        this.push(this.proxyWebResource);

        this.getServerDate = new ServiceEndpointDef('Support', 'GetServerDate');
        this.push(this.getServerDate);
        //#endregion

        //#region Entity/Tag/Taglink endpoints
        this.getEntityLog = new ServiceEndpointDef('EntityLog', 'GetEntityLogHistory');
        this.push(this.getEntityLog);

        this.getLatestEntityLogByUID = new ServiceEndpointDef('EntityLog', 'GetLatestEntityLogByEntityUID');
        this.push(this.getLatestEntityLogByUID);

        this.getEntityUIDByLogUID = new ServiceEndpointDef('EntityLog', 'GetEntityUIDByLogUID');
        this.push(this.getEntityUIDByLogUID);

        this.getAllTags = new ServiceEndpointDef('Tag', 'GetByAll');
        this.push(this.getAllTags);
        this.saveTags = new ServiceEndpointDef('Tag', 'SaveChanges');
        this.push(this.saveTags);

        this.getAllTagLinks = new ServiceEndpointDef('TagLink', 'GetByAll');
        this.push(this.getAllTagLinks);
        this.getTagLinkByTagUID = new ServiceEndpointDef('TagLink', 'GetByTagUID');
        this.push(this.getTagLinkByTagUID);
        this.saveTagLinks = new ServiceEndpointDef('TagLink', 'SaveChanges');
        this.push(this.saveTagLinks);
        //#endregion

        //#region Authentication endpoints...AL
        this.authenticateUser = new ServiceEndpointDef('Authentication', 'Authenticate', false);
        this.push(this.authenticateUser);
        this.switchUser = new ServiceEndpointDef('Authentication', 'SwitchUser');
        this.push(this.switchUser);
        //#endregion

        //#region GridView endpoints...AL
        this.getViewCount = new ServiceEndpointDef('GridView', 'GetViewCount');
        this.push(this.getViewCount);
        this.saveRecords = new ServiceEndpointDef('GridView', 'SaveRecords');
        this.push(this.saveRecords);
        this.getView = new ServiceEndpointDef('GridView', 'GetView');
        this.push(this.getView);
        this.getLazyView = new ServiceEndpointDef('View', 'LazyGet');
        this.push(this.getLazyView);
        this.getPublicForm = new ServiceEndpointDef('View', 'GetPublicForm', false);
        this.push(this.getPublicForm);
        this.getPrivateFormID = new ServiceEndpointDef('View', 'getPrivateFormID');
        this.push(this.getPrivateFormID);
        //#endregion

        //#region DynamicValue endpoints...Al
        this.getByTypeValueID = new ServiceEndpointDef('DynamicValueLog', 'GetByTypeValueID');
        this.push(this.getByTypeValueID);
        this.getByID = new ServiceEndpointDef('DynamicValue', 'GetByID');
        this.push(this.getByID);
        //#endregion

        //#region Package endpoints...AL
        this.getAllPackages = new ServiceEndpointDef('Package', 'GetByAll');
        this.push(this.getAllPackages);
        this.savePackages = new ServiceEndpointDef('Package', 'SaveChanges');
        this.push(this.savePackages);
        this.addTagsToPackages = new ServiceEndpointDef('Package', 'AddTagsToPackages');
        this.push(this.addTagsToPackages);
        this.packageLinkLatestEntitySnapshot = new ServiceEndpointDef('Package', 'InsertSnapshotAsPackageLinks');
        this.push(this.packageLinkLatestEntitySnapshot);
        this.getLogUIDByPackageUID = new ServiceEndpointDef('Package', 'GetLogUIDByPackageUID');
        this.push(this.getLogUIDByPackageUID);
        this.queuePackage = new ServiceEndpointDef('Package', 'QueuePackage');
        this.push(this.queuePackage);
        this.verifyQueuedPackage = new ServiceEndpointDef('Package', "VerifyQueuedPackage");
        this.push(this.verifyQueuedPackage);
        this.pickupPackage = new ServiceEndpointDef('Package', "PickupPackage");
        this.push(this.pickupPackage);
        this.getPackageForDelivery = new ServiceEndpointDef('Package', "GetPackageForDelivery", false);
        this.push(this.getPackageForDelivery);
        //#endregion

        //#region PackageLink endpoints...TD
        this.insertPackageLinksByTagUID = new ServiceEndpointDef('Package', 'InsertPackageLinksByTagUID');
        this.push(this.insertPackageLinksByTagUID);
        this.insertPackageLinks = new ServiceEndpointDef('Package', 'InsertPackageLinks');
        this.push(this.insertPackageLinks);
        this.removePackageLinkByPackageUID = new ServiceEndpointDef('Package', 'RemovePackageLinksByPackageUID');
        this.push(this.removePackageLinkByPackageUID);
        //#endregion

        //#region User Account Control endpoints..PW
        this.resetUserPassword = new ServiceEndpointDef('User', 'ResetUserPassword', false);
        this.push(this.resetUserPassword);
        this.createUserAccount = new ServiceEndpointDef('User', 'CreateUserAccount');
        this.push(this.createUserAccount);
        this.disableUserAccount = new ServiceEndpointDef('User', 'DisableUserAccount');
        this.push(this.disableUserAccount);
        this.modifyUserGroups = new ServiceEndpointDef('User', 'ModifyUserGroups');
        this.push(this.modifyUserGroups);
        this.resetUserAccount = new ServiceEndpointDef('User', 'ResetUserAccount');
        this.push(this.resetUserAccount);
        //#endregion

        //#region Style Endpoints...BC
        this.getAllStyles = new ServiceEndpointDef('Style', 'GetByAll');
        this.push(this.getAllStyles);
        this.saveStyles = new ServiceEndpointDef('Style', 'SaveChanges');
        this.push(this.saveStyles);
        //#endregion

        //#region Value Status Endpoints...WR
        this.getLatestStatusValue = new ServiceEndpointDef("ValueStatus", "GetLatestStatusValue");
        this.push(this.getLatestStatusValue);
        //#endregion

        //#region Record endpoints...AL
        this.linkRecords = new ServiceEndpointDef('Record', 'LinkRecords');
        this.push(this.linkRecords);
        this.unLinkRecords = new ServiceEndpointDef('Record', 'UnLinkRecords');
        this.push(this.unLinkRecords);
        //#endregion

        //#region Notification endpoints..AK
        this.sendEmail = new ServiceEndpointDef('Notify', 'SendEmail');
        this.push(this.sendEmail);
        //#endregion

        //#region Extension endpoints...KB
        this.saveExtensions = new ServiceEndpointDef("Extension", "SaveChanges");
        this.push(this.saveExtensions);
        this.getAllExtensions = new ServiceEndpointDef("Extension", "GetByAll");
        this.push(this.getAllExtensions);
        //#endregion

        /**
         * Create a new endpoint definition object and add as an endpoint. Optional alias is for specific name to use.
         * @param {string} serviceName - The name of the service to call.
         * @param {string} endpointName - The name of the endpoint to call for the service specified.
         * @param {boolean} [authRequired = true] - (OPTIONAL) TRUE, this endpoint will require authentication; FALSE, the endpoint can be called while unauthenticated.
         * @param {string} [alias = ''] - (OPTIONAL) Property name for this service endpoint on the endpoints object.
         */
        this.register = (serviceName, endpointName, authRequired = true, alias = '') => {
            let def = new ServiceEndpointDef(serviceName, endpointName, authRequired);
            def.isCustom = true;

            if (alias == null || alias === '') {
                alias = `${serviceName.toLowerCase()}${endpointName}`;
            }

            if (this[alias] != null && !this[alias].isCustom) {
                throw new Error(`Cannot overwrite non-custom service endpoint ${alias}`);
            }

            this[alias] = def;
            this.push(def);
            return true;
        };

        /**
         * Remove the specified values as if it was an endpoint.
         * @param alias - The name given to the endpoint when registered.
         */
        this.unregister = (alias) => {
            if (this[alias] == null || this.indexOf(alias) === -1) {
                // no service found with that name; could not remove it.
                return false;
            }

            // check if this is a custom endpoint; if not, then we should not allow them to remove it.
            if (this[alias].isCustom) {
                this.remove(this[alias]);
                delete this[alias];
                return true;
            }

            return false;
        };
    }
}

export default new Endpoints();
